/**
 * Tipi della libreria dei test di misura e passaggi fra form e parametri.
 *
 * I campi del form sono testi, con la virgola dei decimali; i parametri sono
 * numeri, validati da `parametriTestSchema`. La conversione sta qui, fuori
 * dalle viste, perché la usano form, anteprima e (slice 06) l'esecuzione.
 */

import {
  calcolaPunteggio,
  parametriTestSchema,
  risultatoTestSchema,
  type FamigliaAttributo,
  type MetodoTest,
  type ParametriTest,
  type Punteggio,
} from '@calcio/shared';

export interface AttributoDelTest {
  id: string;
  nome: string;
  famiglia: FamigliaAttributo;
  attivo: boolean;
}

export interface MisuraInElenco {
  id: string;
  nome: string;
  unita: string | null;
  metodo: MetodoTest;
  attributi: { id: string; nome: string; famiglia: FamigliaAttributo }[];
}

export interface TestInElenco {
  id: string;
  nome: string;
  rpeRiferimento: number | null;
  minutiRiferimento: number | null;
  attivo: boolean;
  misure: MisuraInElenco[];
  /** Esecuzioni di tutte le squadre della società. */
  esecuzioni: number;
  ultimaEsecuzione: string | null;
}

export interface MisuraDelTest {
  id: string;
  ordine: number;
  nome: string;
  unita: string | null;
  metodo: MetodoTest;
  parametri: ParametriTest;
  attributi: AttributoDelTest[];
}

export interface TestDettaglio {
  id: string;
  nome: string;
  istruzioni: string | null;
  rpeRiferimento: number | null;
  minutiRiferimento: number | null;
  attivo: boolean;
  misure: MisuraDelTest[];
}

/** I metodi delle misure, senza ripetizioni, nell'ordine delle misure. */
export function metodiDelTest(t: { misure: { metodo: MetodoTest }[] }): MetodoTest[] {
  return [...new Set(t.misure.map((m) => m.metodo))];
}

/** L'unità di un campo: quella del test, o fissa (metri, secondi), o nessuna. */
export type UnitaCampo = 'test' | 'm' | 's' | null;

export interface Campo {
  chiave: string;
  etichetta: string;
  unita: UnitaCampo;
}

/** Campi dei parametri per metodo; le navette del gradini hanno un editor a sé. */
export const CAMPI_PARAMETRI: Record<MetodoTest, Campo[]> = {
  percentuale: [{ chiave: 'massimo', etichetta: 'Massimo (vale 100)', unita: 'test' }],
  diretta: [
    { chiave: 'minimo', etichetta: 'Minimo (vale 1)', unita: 'test' },
    { chiave: 'massimo', etichetta: 'Massimo (vale 100)', unita: 'test' },
  ],
  inversa: [
    { chiave: 'migliore', etichetta: 'Migliore (vale 100)', unita: 'test' },
    { chiave: 'peggiore', etichetta: 'Peggiore (vale 1)', unita: 'test' },
  ],
  target: [
    { chiave: 'target', etichetta: 'Target (vale 100)', unita: 'test' },
    { chiave: 'tolleranza', etichetta: 'Tolleranza (lo scarto che vale 1)', unita: 'test' },
  ],
  gradini: [
    { chiave: 'lunghezzaNavetta', etichetta: 'Lunghezza della navetta', unita: 'm' },
    { chiave: 'metriMinimi', etichetta: 'Metri minimi (vale 1)', unita: 'm' },
    { chiave: 'metriMassimi', etichetta: 'Metri massimi (vale 100)', unita: 'm' },
  ],
  penalita: [
    { chiave: 'penalita', etichetta: 'Penalità per errore', unita: 's' },
    { chiave: 'migliore', etichetta: 'Tempo netto migliore (vale 100)', unita: 's' },
    { chiave: 'peggiore', etichetta: 'Tempo netto peggiore (vale 1)', unita: 's' },
  ],
};

/** Campi del risultato di un giocatore, per l'anteprima e per l'esecuzione. */
export const CAMPI_RISULTATO: Record<MetodoTest, Campo[]> = {
  percentuale: [{ chiave: 'valore', etichetta: 'Risultato', unita: 'test' }],
  diretta: [{ chiave: 'valore', etichetta: 'Risultato', unita: 'test' }],
  inversa: [{ chiave: 'valore', etichetta: 'Risultato', unita: 'test' }],
  target: [{ chiave: 'valore', etichetta: 'Risultato', unita: 'test' }],
  gradini: [
    { chiave: 'livello', etichetta: 'Livello', unita: null },
    { chiave: 'navetta', etichetta: 'Navetta', unita: null },
  ],
  penalita: [
    { chiave: 'tempo', etichetta: 'Tempo', unita: 's' },
    { chiave: 'errori', etichetta: 'Errori', unita: null },
  ],
};

export interface NavetteForm {
  /** Falso: `uguali` vale per ogni livello. Vero: `lista`, una per livello. */
  perLivello: boolean;
  uguali: string;
  lista: string[];
}

export interface FormParametri {
  metodo: MetodoTest;
  valori: Record<string, string>;
  navette: NavetteForm;
}

/** Testo del campo in numero: la virgola vale il punto, il vuoto resta vuoto. */
export function numeroDaCampo(testo: string | undefined): number | undefined {
  const t = testo?.trim() ?? '';
  return t === '' ? undefined : Number(t.replace(',', '.'));
}

const testoDaNumero = (n: number) => String(n).replace('.', ',');

/** I parametri da validare: solo i campi del metodo scelto. */
export function parametriDaForm({ metodo, valori, navette }: FormParametri): unknown {
  const campi = Object.fromEntries(
    CAMPI_PARAMETRI[metodo].map((c) => [c.chiave, numeroDaCampo(valori[c.chiave])]),
  );
  if (metodo !== 'gradini') return { metodo, ...campi };
  const lista = navette.perLivello
    ? navette.lista.filter((n) => n.trim() !== '').map((n) => numeroDaCampo(n))
    : [numeroDaCampo(navette.uguali)];
  return { metodo, ...campi, navettePerLivello: lista };
}

export function formDaParametri(p: ParametriTest): FormParametri {
  const valori: Record<string, string> = {};
  for (const c of CAMPI_PARAMETRI[p.metodo]) {
    const v = (p as unknown as Record<string, number>)[c.chiave];
    if (typeof v === 'number') valori[c.chiave] = testoDaNumero(v);
  }
  const lista = p.metodo === 'gradini' ? p.navettePerLivello.map(testoDaNumero) : [''];
  return {
    metodo: p.metodo,
    valori,
    navette: { perLivello: lista.length > 1, uguali: lista[0] ?? '', lista },
  };
}

export function risultatoDaForm(
  metodo: MetodoTest,
  valori: Record<string, string>,
): Record<string, number | undefined> {
  return Object.fromEntries(
    CAMPI_RISULTATO[metodo].map((c) => [c.chiave, numeroDaCampo(valori[c.chiave])]),
  );
}

/** Punteggio di un risultato di prova, o `null` finché qualcosa non torna. */
export function anteprima(
  form: FormParametri,
  risultato: Record<string, string>,
): Punteggio | null {
  const parametri = parametriTestSchema.safeParse(parametriDaForm(form));
  if (!parametri.success) return null;
  const r = risultatoTestSchema(parametri.data).safeParse(risultatoDaForm(form.metodo, risultato));
  if (!r.success) return null;
  return calcolaPunteggio(parametri.data, r.data as Record<string, number>);
}

const n = (v: number) => v.toLocaleString('it-IT');
const conUnita = (v: number, unita: string | null) => (unita ? `${n(v)} ${unita}` : n(v));

/** La regola del test in una frase: che cosa vale 100 e che cosa vale 1. */
export function formulaAParole(p: ParametriTest, unita: string | null): string {
  switch (p.metodo) {
    case 'percentuale':
      return `${conUnita(p.massimo, unita)} vale 100, 0 vale 1; in mezzo in proporzione.`;
    case 'diretta':
      return `${conUnita(p.minimo, unita)} vale 1 e ${conUnita(p.massimo, unita)} vale 100: più è meglio.`;
    case 'inversa':
      return `${conUnita(p.migliore, unita)} vale 100 e ${conUnita(p.peggiore, unita)} vale 1: meno è meglio.`;
    case 'target':
      return `${conUnita(p.target, unita)} vale 100; ${conUnita(p.tolleranza, unita)} di scarto, sopra o sotto, vale 1.`;
    case 'gradini': {
      const navette =
        p.navettePerLivello.length === 1
          ? `${n(p.navettePerLivello[0]!)} per livello`
          : `per livello ${p.navettePerLivello.map(n).join(', ')}`;
      return `Navette da ${n(p.lunghezzaNavetta)} m, ${navette}; ${n(p.metriMinimi)} m vale 1 e ${n(p.metriMassimi)} m vale 100.`;
    }
    case 'penalita':
      return `Ogni errore aggiunge ${n(p.penalita)} s; ${n(p.migliore)} s netti vale 100 e ${n(p.peggiore)} s vale 1.`;
  }
}

/** L'unità da mostrare accanto a un campo. */
export const unitaDelCampo = (c: Campo, unitaTest: string | null) =>
  c.unita === 'test' ? unitaTest : c.unita;

/**
 * Errori di `creaTestSchema` per campo, il primo per ciascuno. Quelli di una
 * misura si chiamano `misure.<i>.<campo>`, e i suoi parametri
 * `misure.<i>.parametri.<campo>`; `misure` da solo è l'elenco intero.
 */
export function erroriDelTest(errore: {
  issues: { path: (string | number)[]; message: string }[];
}): Record<string, string> {
  const esito: Record<string, string> = {};
  for (const { path, message } of errore.issues) {
    const [primo, indice, campo, sotto] = path;
    let chiave = String(primo ?? '');
    if (primo === 'misure' && indice !== undefined && campo !== undefined) {
      chiave =
        campo === 'parametri' && sotto !== undefined
          ? `misure.${indice}.parametri.${sotto}`
          : `misure.${indice}.${campo}`;
    }
    esito[chiave] ??= message;
  }
  return esito;
}

// ---------------------------------------------------------------------------
// Form del test con le sue misure
// ---------------------------------------------------------------------------

export interface FormMisura {
  /** Chiave della card nel form, anche per le misure non ancora salvate. */
  chiave: string;
  /** L'id della misura salvata; manca per quelle nuove. */
  id?: string;
  nome: string;
  /** Finché è falso la prima misura porta il nome del test. */
  nomeToccato: boolean;
  unita: string;
  parametri: FormParametri;
  attributi: string[];
}

export interface FormTest {
  nome: string;
  istruzioni: string;
  rpe: string;
  minuti: string;
  misure: FormMisura[];
}

let prossimaChiave = 0;

export function nuovaMisura(): FormMisura {
  prossimaChiave += 1;
  return {
    chiave: `misura-${prossimaChiave}`,
    id: undefined,
    nome: '',
    nomeToccato: false,
    unita: '',
    parametri: {
      metodo: 'inversa',
      valori: {},
      navette: { perLivello: false, uguali: '', lista: [''] },
    },
    attributi: [],
  };
}

/** Il nome della misura `i`: la prima, finché non la si tocca, è quello del test. */
export function nomeMisura(f: FormTest, i: number): string {
  const m = f.misure[i]!;
  return i === 0 && !m.nomeToccato ? f.nome : m.nome;
}

/** Il corpo da validare con `creaTestSchema` e mandare al server. */
export function corpoDelTest(f: FormTest) {
  const numero = (t: string) => numeroDaCampo(t) ?? null;
  return {
    nome: f.nome,
    istruzioni: f.istruzioni,
    rpeRiferimento: numero(f.rpe),
    minutiRiferimento: numero(f.minuti),
    misure: f.misure.map((m, i) => ({
      ...(m.id && { id: m.id }),
      nome: nomeMisura(f, i),
      unita: m.unita,
      parametri: parametriDaForm(m.parametri),
      attributi: m.attributi,
    })),
  };
}

export function formDaTest(t: TestDettaglio): FormTest {
  return {
    nome: t.nome,
    istruzioni: t.istruzioni ?? '',
    rpe: t.rpeRiferimento === null ? '' : String(t.rpeRiferimento),
    minuti: t.minutiRiferimento === null ? '' : String(t.minutiRiferimento),
    misure: t.misure.map((m, i) => ({
      chiave: `misura-${m.id}`,
      id: m.id,
      nome: m.nome,
      nomeToccato: i > 0 || m.nome !== t.nome,
      unita: m.unita ?? '',
      parametri: formDaParametri(m.parametri),
      attributi: m.attributi.map((a) => a.id),
    })),
  };
}

/** La misura `i` spostata di `delta` posti; ai bordi resta dov'è. */
export function spostaMisura(misure: FormMisura[], i: number, delta: number): FormMisura[] {
  const j = i + delta;
  if (j < 0 || j >= misure.length) return misure;
  const nuove = [...misure];
  [nuove[i], nuove[j]] = [nuove[j]!, nuove[i]!];
  return nuove;
}

/** Gli attributi delle altre misure, con il nome della misura che li ha. */
export function attributiAltrove(f: FormTest, i: number): Map<string, string> {
  const altrove = new Map<string, string>();
  f.misure.forEach((m, j) => {
    if (j === i) return;
    for (const a of m.attributi) altrove.set(a, nomeMisura(f, j));
  });
  return altrove;
}

/** Una riga per metodo, sotto la tendina del form. */
export const DESCRIZIONE_METODO: Record<MetodoTest, string> = {
  percentuale: 'Il risultato diviso il massimo: 15 tiri a bersaglio su 30 fanno 50.',
  diretta: 'Più è meglio: il minimo vale 1, il massimo 100, in mezzo in proporzione.',
  inversa: 'Meno è meglio, come un tempo: il migliore vale 100, il peggiore 1.',
  target: 'Il centro è meglio: il target vale 100, e il punteggio scende allontanandosi.',
  gradini: 'Livello e navetta diventano metri percorsi, poi come la diretta: Yo-Yo, Beep test.',
  penalita:
    'Ogni errore aggiunge secondi al tempo, poi come l’inversa sul tempo netto: slalom, T-Test.',
};
